import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  LogOut, Search, Bookmark, BookmarkCheck,
  Filter, Loader2, FileText, ChevronLeft
} from 'lucide-react';

export default function StaffSearch() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    city: '', minExperience: '', staffTypeId: '', domainId: '',
    page: 0, size: 10,
  });
  const [shortlistLoading, setShortlistLoading] = useState({});
  const [hireLoading, setHireLoading] = useState({});

  const handleLogout = () => { logout(); navigate('/company/login'); };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value, page: 0 });
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      // Remove empty params
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== '' && v !== null)
      );
      const response = await companyAPI.searchStaff(params);
      setResults(response.data.data || []);
      if ((response.data.data || []).length === 0) {
        toast.info('No staff found matching your criteria');
      }
    } catch (error) {
      toast.error('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Search on first load
  useEffect(() => { handleSearch(); }, []);

  const toggleShortlist = async (staff) => {
    setShortlistLoading((prev) => ({ ...prev, [staff.staffId]: true }));
    try {
      if (staff.shortlisted) {
        await companyAPI.removeShortlist(staff.staffId);
        toast.success('Removed from shortlist');
      } else {
        await companyAPI.addShortlist(staff.staffId);
        toast.success('Added to shortlist');
      }
      // Toggle locally
      setResults((prev) =>
        prev.map((s) =>
          s.staffId === staff.staffId ? { ...s, shortlisted: !s.shortlisted } : s
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.message || 'Action failed');
    } finally {
      setShortlistLoading((prev) => ({ ...prev, [staff.staffId]: false }));
    }
  };

  const sendHireRequest = async (staffId) => {
    setHireLoading((prev) => ({ ...prev, [staffId]: true }));
    try {
      await companyAPI.createHireRequest({ staffId, message: '' });
      toast.success('Hire request sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send hire request');
    } finally {
      setHireLoading((prev) => ({ ...prev, [staffId]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/company/dashboard')}
              className="text-gray-400 hover:text-gray-600 transition">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h1 className="text-xl font-bold text-blue-700">ShramSena</h1>
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">COMPANY</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/company/shortlist')}
              className="flex items-center gap-1 text-purple-600 hover:text-purple-800 text-sm font-medium transition">
              <Bookmark className="h-4 w-4" /> Shortlist
            </button>
            <button onClick={() => navigate('/company/hire-requests')}
              className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm font-medium transition">
              <FileText className="h-4 w-4" /> Hire Requests
            </button>
            <button onClick={handleLogout}
              className="flex items-center gap-1 text-red-500 hover:text-red-700 transition text-sm">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-6">
          <Search className="h-6 w-6 text-blue-600" />
          <h2 className="text-2xl font-semibold text-gray-800">Search Staff</h2>
        </div>

        {/* Filters */}
        <form onSubmit={handleSearch}
          className="bg-white rounded-xl border shadow-sm p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Filters</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
              <input name="city" value={filters.city} onChange={handleFilterChange}
                placeholder="e.g. Mumbai"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Min Experience (yrs)</label>
              <input name="minExperience" type="number" min="0"
                value={filters.minExperience} onChange={handleFilterChange}
                placeholder="e.g. 2"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="sm:col-span-2 flex items-end">
              <button type="submit" disabled={loading}
                className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-60">
                {loading
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Searching...</>
                  : <><Search className="h-4 w-4" /> Search</>}
              </button>
            </div>
          </div>
        </form>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <Search className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">No staff found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {results.map((staff) => (
              <div key={staff.staffId}
                className="bg-white rounded-xl border shadow-sm p-5 flex flex-col gap-3">

                {/* Name + shortlist */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      {staff.firstName} {staff.lastName}
                    </h3>
                    <p className="text-xs text-blue-600 font-medium mt-0.5">
                      {staff.staffTypeName}
                      {staff.domainName ? ` · ${staff.domainName}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleShortlist(staff)}
                    disabled={shortlistLoading[staff.staffId]}
                    className="text-purple-500 hover:text-purple-700 transition disabled:opacity-50">
                    {shortlistLoading[staff.staffId]
                      ? <Loader2 className="h-5 w-5 animate-spin" />
                      : staff.shortlisted
                        ? <BookmarkCheck className="h-5 w-5 text-purple-600" />
                        : <Bookmark className="h-5 w-5" />}
                  </button>
                </div>

                {/* Details */}
                <div className="flex flex-wrap gap-2">
                  {staff.age != null && (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                      {staff.age} yrs old
                    </span>
                  )}
                  {staff.placementStatus === 'HIRED' && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">
                      Currently Hired
                    </span>
                  )}
                  {staff.yearsOfExperience != null && (
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                      {staff.yearsOfExperience} yr{staff.yearsOfExperience !== 1 ? 's' : ''} exp
                    </span>
                  )}
                  {staff.qualification && (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                      {staff.qualification}
                    </span>
                  )}
                  {staff.city && (
                    <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs">
                      {staff.city}{staff.state ? `, ${staff.state}` : ''}
                    </span>
                  )}
                  {staff.availabilityType && (
                    <span className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">
                      {staff.availabilityType.replace(/_/g, ' ')}
                    </span>
                  )}
                  {staff.expectedSalaryRange && (
                    <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full text-xs font-medium">
                      {staff.expectedSalaryRange}
                    </span>
                  )}
                </div>

                {/* Hire button */}
                <button
                  onClick={() => sendHireRequest(staff.staffId)}
                  disabled={hireLoading[staff.staffId]}
                  className="mt-auto w-full bg-green-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition flex items-center justify-center gap-2 disabled:opacity-60">
                  {hireLoading[staff.staffId]
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
                    : <><FileText className="h-4 w-4" /> Send Hire Request</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
